import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Plus, RotateCw, Trash2, UserPlus, Settings, Save, Target, Store, Building2, Pencil, X, Check, Wallet, Download, Shield, Database, Bug, Lightbulb, Radio, CloudUpload, Sparkles, Layers, Car } from 'lucide-react';
import UserAdminTable from '@/components/UserAdminTable';
import RolePermissionsMatrix from '@/components/RolePermissionsMatrix';
import RevenueChannelsTab from '@/components/RevenueChannelsTab';
import SnapshotManager from '@/components/SnapshotManager';
import PreviewToProdSyncPanel from '@/components/PreviewToProdSyncPanel';
import AudioPrewarmPanel from '@/components/AudioPrewarmPanel';
import AudioTranslationsList from '@/components/AudioTranslationsList';
import RadioDropsPanel from '@/components/RadioDropsPanel';
import RadioTemplatesPanel from '@/components/RadioTemplatesPanel';
import RadioPhoneImportPanel from '@/components/RadioPhoneImportPanel';
import VaultPanel from '@/components/VaultPanel';
import PanelErrorBoundary from '@/components/PanelErrorBoundary';
import IssueInbox from '@/components/IssueInbox';
import WishlistInbox from '@/components/WishlistInbox';
import OperatorMiraPanel from '@/components/OperatorMiraPanel';
import OperatorMiraDoctrineTile from '@/components/OperatorMiraDoctrineTile';
import MiraFoundersKitTile from '@/components/MiraFoundersKitTile';
import ExternalApiKeysTile from '@/components/ExternalApiKeysTile';
import SoulCsvExportTile from '@/components/SoulCsvExportTile';
import SoulWelcomeInviteTile from '@/components/SoulWelcomeInviteTile';
import SoulRichnessLeaderboardTile from '@/components/SoulRichnessLeaderboardTile';
import ShareableDocsTile from '@/components/ShareableDocsTile';
import CronHeartbeatTile from '@/components/CronHeartbeatTile';
import { useAuth } from '@/contexts/AuthContext';

const ROLE_OPTIONS = [
  { value: 'ceo', label: 'CEO' },
  { value: 'leader', label: 'Division Leader' },
  { value: 'sub_leader', label: 'Sub-Leader' },
  { value: 'submitter', label: 'Data Entry / Submitter' },
];

const THRESHOLD_LABELS = {
  revenue_drop_wow_warning: { label: 'Revenue drop WoW — warning', unit: '%', desc: 'Week-over-week decline triggers amber alert' },
  revenue_drop_wow_critical: { label: 'Revenue drop WoW — critical', unit: '%', desc: 'Week-over-week decline triggers red alert + CEO notification' },
  revenue_drop_mom_critical: { label: 'Revenue drop MoM — critical', unit: '%', desc: 'Month-over-month decline triggers red alert' },
  daily_run_rate_red_pct: { label: 'Daily run rate danger zone', unit: '%', desc: 'When needed run rate exceeds this % of normal, flag red' },
  contract_expiry_warning_days: { label: 'Contract expiry warning', unit: 'days', desc: 'Alert when contract expires within this many days' },
  kpi_amber_weeks_alert: { label: 'KPI amber streak alert', unit: 'weeks', desc: 'Alert when a KPI stays amber for consecutive weeks' },
  overdue_deadline_hour: { label: 'Weekly update deadline', unit: 'hour (IST)', desc: 'Hour by which Monday update should be submitted' },
};

const fmt = (v) => { if (!v) return '0'; v = Number(v); if (v >= 10000000) return `${(v/10000000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}Cr`; if (v >= 100000) return `${(v/100000).toFixed(1)}L`; return v.toLocaleString('en-IN'); };

const TABS = [
  { id: 'users', label: 'Users', icon: UserPlus },
  { id: 'permissions', label: 'Permissions', icon: Shield },
  { id: 'targets', label: 'Targets', icon: Target },
  { id: 'outlets', label: 'Outlets', icon: Store },
  { id: 'divisions', label: 'Divisions', icon: Building2 },
  { id: 'channels', label: 'Revenue Channels', icon: Layers },
  { id: 'expenses', label: 'Expenses', icon: Wallet },
  { id: 'thresholds', label: 'Thresholds', icon: Settings },
  { id: 'backup', label: 'Backup', icon: Database },
  { id: 'vault', label: 'Vault', icon: CloudUpload },
  { id: 'radio', label: 'Radio', icon: Radio },
  { id: 'jlr', label: 'JLR', icon: Car },
  { id: 'mira', label: 'Operator Mira', icon: Sparkles },
  { id: 'issues', label: 'Issues', icon: Bug },
  { id: 'wishlist', label: 'Wishlist', icon: Lightbulb },
];

export default function AdminPanel() {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get('tab');
    if (t && ['users','divisions','outlets','permissions','jlr','mira','issues','wishlist','backup','vault','radio'].includes(t)) {
      return t;
    }
    return 'users';
  })();
  const initialPrefill = searchParams.get('prefill') || '';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [miraPrefill, setMiraPrefill] = useState(initialPrefill);

  // When the tab changes via UI clicks, keep the URL in sync so deep
  // links round-trip cleanly. Clear `prefill` so a later visit to the
  // Mira tab doesn't re-inject a stale command.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', activeTab);
    if (activeTab !== 'mira') next.delete('prefill');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [allOutlets, setAllOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'leader', division_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [thresholds, setThresholds] = useState({});
  const [saved, setSaved] = useState('');
  const [selectedDiv, setSelectedDiv] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState({});
  const [outletForm, setOutletForm] = useState(null);
  const [divEdit, setDivEdit] = useState(null);
  const [newRevLine, setNewRevLine] = useState(null);

  const fetchAll = async () => {
    const [uRes, dListRes, tRes] = await Promise.all([
      api.get('/admin/users'),
      api.get('/divisions'),
      api.get('/settings/thresholds').catch(() => ({ data: {} })),
    ]);
    setUsers(uRes.data);
    setThresholds(tRes.data);
    // Fetch full division details (with quantitative_fields) for each
    const fullDivs = await Promise.all(
      dListRes.data.filter(d => !d.hidden).map(async d => {
        try {
          const r = await api.get(`/divisions/${d.id}`);
          return { ...d, ...r.data };
        } catch { return d; }
      })
    );
    setDivisions(fullDivs);
    if (!selectedDiv && fullDivs.length > 0) setSelectedDiv(fullDivs[0].id);
    const oRes = await api.get('/outlets');
    setAllOutlets(oRes.data);
  };

  useEffect(() => { fetchAll().finally(() => setLoading(false)); }, []);

  const flash = (msg) => { setSaved(msg); setTimeout(() => setSaved(''), 2000); };
  const handleCreate = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await api.post('/admin/users', form); setShowForm(false); setForm({ name: '', email: '', role: 'leader', division_id: '' }); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };
  const handleResetPassword = async (userId, userName) => { if (!window.confirm(`Reset password for ${userName}?`)) return; await api.post(`/admin/users/${userId}/reset-password`); alert('Password reset to Welcome@2026'); };
  const handleDelete = async (userId, userName) => { if (!window.confirm(`Delete user ${userName}?`)) return; await api.delete(`/admin/users/${userId}`); fetchAll(); };
  const saveThresholds = async () => { await api.put('/settings/thresholds', thresholds); flash('Thresholds saved!'); };

  // === TARGET EDITING ===
  const selectedDivData = divisions.find(d => d.id === selectedDiv);
  const revFields = (selectedDivData?.quantitative_fields || []).filter(f => f.is_revenue);

  const startEditField = (field) => {
    setEditingField(field.key);
    setEditValue({
      target_annual: field.target_annual || 0,
      target_q1: field.target_q1 || 0, target_q2: field.target_q2 || 0,
      target_q3: field.target_q3 || 0, target_q4: field.target_q4 || 0,
      owner: field.owner || '',
    });
  };

  const saveFieldTarget = async () => {
    if (!selectedDivData || !editingField) return;
    const updatedFields = selectedDivData.quantitative_fields.map(f => {
      if (f.key === editingField) {
        return { ...f, ...editValue, target_annual: Number(editValue.target_annual) || 0,
          target_q1: Number(editValue.target_q1) || 0, target_q2: Number(editValue.target_q2) || 0,
          target_q3: Number(editValue.target_q3) || 0, target_q4: Number(editValue.target_q4) || 0 };
      }
      return f;
    });
    await api.put(`/divisions/${selectedDiv}`, { quantitative_fields: updatedFields });
    setEditingField(null);
    fetchAll();
    flash('Target saved! Division total updated.');
  };

  const addRevenueLine = async () => {
    if (!newRevLine || !newRevLine.label?.trim()) return;
    const key = 'rev_' + newRevLine.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');
    const newField = {
      key, label: newRevLine.label.trim(), owner: newRevLine.owner || '',
      type: 'currency', is_revenue: true,
      target_annual: Number(newRevLine.target_annual) || 0,
      target_q1: Number(newRevLine.target_q1) || 0, target_q2: Number(newRevLine.target_q2) || 0,
      target_q3: Number(newRevLine.target_q3) || 0, target_q4: Number(newRevLine.target_q4) || 0,
    };
    const updatedFields = [...(selectedDivData.quantitative_fields || []), newField];
    await api.put(`/divisions/${selectedDiv}`, { quantitative_fields: updatedFields });
    setNewRevLine(null);
    fetchAll();
    flash(`"${newField.label}" added! Division total updated.`);
  };

  const deleteRevenueLine = async (fieldKey, label) => {
    if (!window.confirm(`Remove "${label}" revenue line? This removes the target — daily sales data is preserved.`)) return;
    const updatedFields = (selectedDivData.quantitative_fields || []).filter(f => f.key !== fieldKey);
    await api.put(`/divisions/${selectedDiv}`, { quantitative_fields: updatedFields });
    fetchAll();
    flash(`"${label}" removed.`);
  };

  // === OUTLET MANAGEMENT ===
  const divOutlets = allOutlets.filter(o => o.division_id === selectedDiv);

  const saveOutlet = async () => {
    if (!outletForm) return;
    // Strip the helper-only field before POST/PUT — it's UI state, not persistence.
    const { __clone_source, ...payload } = outletForm;
    if (outletForm.id) {
      await api.put(`/outlets/${outletForm.id}`, payload);
    } else {
      await api.post('/outlets', { ...payload, division_id: selectedDiv });
    }
    setOutletForm(null);
    fetchAll();
    flash(__clone_source ? 'Outlet cloned & saved!' : 'Outlet saved!');
  };

  const deleteOutlet = async (id, name) => {
    if (!window.confirm(`Delete outlet "${name}"?`)) return;
    await api.delete(`/outlets/${id}`);
    fetchAll();
  };

  // === DIVISION SETTINGS ===
  const saveDivision = async () => {
    if (!divEdit) return;
    if (divEdit.isNew) {
      await api.post('/divisions', {
        name: divEdit.name, leader_name: divEdit.leader_name,
        entity_name: divEdit.entity_name, color: divEdit.color,
        parent_division_id: divEdit.parent_division_id || null,
      });
      flash(`Division "${divEdit.name}" created!`);
    } else {
      await api.put(`/divisions/${divEdit.id}`, {
        name: divEdit.name, leader_name: divEdit.leader_name,
        entity_name: divEdit.entity_name, color: divEdit.color,
        parent_division_id: divEdit.parent_division_id || null,
      });
      flash('Division updated!');
    }
    setDivEdit(null);
    fetchAll();
  };

  const hideDivision = async (id, name) => {
    if (!window.confirm(
      `Hide division "${name}"?\n\n` +
      "This soft-hides it from pickers but preserves historical data. " +
      "Fails if any user is still assigned. Restorable later.")) return;
    try {
      await api.delete(`/divisions/${id}`);
      flash(`Division "${name}" hidden.`);
      fetchAll();
    } catch (e) {
      alert('Hide failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-panel">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-3 py-2.5 text-xs font-medium border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === t.id ? '' : 'border-transparent'}`}
            style={activeTab === t.id ? { color: '#1B2A4A', borderColor: '#1B2A4A' } : { color: '#6B7280' }}
            data-testid={`admin-tab-${t.id}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* Saved flash */}
      {saved && <div className="rounded-md px-3 py-2 text-xs font-medium" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }} data-testid="save-flash">{saved}</div>}

      {/* === USERS TAB === */}
      {activeTab === 'users' && (
        <UserAdminTable users={users} divisions={divisions} onRefresh={fetchAll} />
      )}

      {/* === PERMISSIONS TAB === */}
      {activeTab === 'permissions' && <RolePermissionsMatrix currentUser={currentUser} />}

      {/* === BACKUP TAB === */}
      {activeTab === 'backup' && (
        <div className="space-y-4" data-testid="backup-tab">
          <PanelErrorBoundary label="Operator Mira Doctrine"><OperatorMiraDoctrineTile /></PanelErrorBoundary>
          <PanelErrorBoundary label="Snapshot Manager"><SnapshotManager /></PanelErrorBoundary>
          <PanelErrorBoundary label="Audio Pre-warm"><AudioPrewarmPanel /></PanelErrorBoundary>
          <PanelErrorBoundary label="Translation Library"><AudioTranslationsList /></PanelErrorBoundary>
        </div>
      )}

      {/* === ISSUES TAB === */}
      {activeTab === 'issues' && <IssueInbox />}
      {activeTab === 'vault' && (
        <div className="space-y-4" data-testid="vault-tab">
          <PanelErrorBoundary label="Preview → Production Sync"><PreviewToProdSyncPanel /></PanelErrorBoundary>
          <PanelErrorBoundary label="SiteVault"><VaultPanel /></PanelErrorBoundary>
        </div>
      )}

      {activeTab === 'radio' && (
        <div className="space-y-4">
          <PanelErrorBoundary label="Bulk Phone Import"><RadioPhoneImportPanel /></PanelErrorBoundary>
          <PanelErrorBoundary label="Radio Drops"><RadioDropsPanel /></PanelErrorBoundary>
          <PanelErrorBoundary label="WhatsApp Templates"><RadioTemplatesPanel /></PanelErrorBoundary>
        </div>
      )}

      {activeTab === 'mira' && (
        <div className="space-y-4" data-testid="mira-tab">
          <PanelErrorBoundary label="Cron Heartbeat"><CronHeartbeatTile /></PanelErrorBoundary>
          <PanelErrorBoundary label="Doctrine"><OperatorMiraDoctrineTile /></PanelErrorBoundary>
          <PanelErrorBoundary label="Founders Kit"><MiraFoundersKitTile /></PanelErrorBoundary>
          <PanelErrorBoundary label="Shareable Docs"><ShareableDocsTile /></PanelErrorBoundary>
          <PanelErrorBoundary label="External API Keys"><ExternalApiKeysTile /></PanelErrorBoundary>
          <PanelErrorBoundary label="Operator Mira">
            <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
              <OperatorMiraPanel initialPrefill={miraPrefill} onPrefillConsumed={() => setMiraPrefill('')} />
            </div>
          </PanelErrorBoundary>
        </div>
      )}

      {activeTab === 'jlr' && (
        <div className="space-y-4" data-testid="jlr-tab">
          {/* Header card — explains what this tab is for. Doctrine: a
              dedicated client surface (per Dipali · 15-May-2026). Aabe /
              DreamFolks / Xiaomi will get their own equivalent tabs
              when the time comes — keeping a clean separation. */}
          <div className="rounded-xl border p-5"
            style={{ borderColor: '#E5E1DC',
                     background: 'linear-gradient(135deg, #1B2A4A 0%, #2A3F6E 100%)',
                     color: '#FFFFFF' }}
            data-testid="jlr-tab-header">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#B88C40' }}>
                <Car size={22} style={{ color: '#1B2A4A' }} />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase mb-1"
                   style={{ color: '#B88C40' }}>
                  Jaguar Land Rover · Member Onboarding
                </p>
                <h2 className="text-lg font-semibold" style={{ color: '#FFFFFF' }}>
                  Welcome a new JLR member to Concierge®
                </h2>
                <p className="text-[12px] mt-1.5" style={{ color: '#C9C2B8' }}>
                  Single or bulk — add their details, click send. They get the
                  Welcome Letter from <strong style={{ color: '#F5C86B' }}>request@concierges.in</strong>,
                  membership login on top, personal Soul Profile link below.
                  Replies land at the team inbox.
                </p>
              </div>
            </div>
          </div>

          <PanelErrorBoundary label="JLR Welcome Letter">
            <SoulWelcomeInviteTile fixedClientId="jlr" />
          </PanelErrorBoundary>
          <PanelErrorBoundary label="JLR Soul CSV Export">
            <SoulCsvExportTile fixedClientId="jlr" />
          </PanelErrorBoundary>
          <PanelErrorBoundary label="JLR Soul-richness Leaderboard">
            <SoulRichnessLeaderboardTile fixedClientId="jlr" />
          </PanelErrorBoundary>
        </div>
      )}

      {/* === WISHLIST TAB === */}
      {activeTab === 'wishlist' && <WishlistInbox />}

      {/* === TARGETS TAB === */}
      {activeTab === 'targets' && (
        <div className="space-y-4" data-testid="targets-tab">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Revenue Targets</h2>
            <div className="flex items-center gap-2">
              <select value={selectedDiv} onChange={e => setSelectedDiv(e.target.value)}
                className="px-3 py-1.5 rounded-md border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid="target-div-select">
                {divisions.filter(d => !d.hidden).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <button onClick={() => setNewRevLine({ label: '', owner: '', target_annual: '', target_q1: '', target_q2: '', target_q3: '', target_q4: '' })}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-rev-line-btn">
                <Plus size={12} /> Add Revenue Line
              </button>
            </div>
          </div>

          {selectedDivData && (
            <>
              <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }}>
                <p className="text-xs" style={{ color: '#6B7280' }}>Division total (auto-calculated from outlets below):</p>
                <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{fmt(selectedDivData.annual_target)}</p>
                <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Q1: {fmt(selectedDivData.quarterly_targets?.[0])} | Q2: {fmt(selectedDivData.quarterly_targets?.[1])} | Q3: {fmt(selectedDivData.quarterly_targets?.[2])} | Q4: {fmt(selectedDivData.quarterly_targets?.[3])}</p>
              </div>

              {/* Add Revenue Line form */}
              {newRevLine && (
                <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="new-rev-line-form">
                  <div className="flex items-center justify-between"><p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>New Revenue Line</p><button onClick={() => setNewRevLine(null)}><X size={14} style={{ color: '#6B7280' }} /></button></div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Name *</label><input value={newRevLine.label} onChange={e => setNewRevLine(f => ({...f, label: e.target.value}))} placeholder="e.g. Xin (Whitefield)" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-rev-label" /></div>
                    <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Owner</label><input value={newRevLine.owner} onChange={e => setNewRevLine(f => ({...f, owner: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-rev-owner" /></div>
                    <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Annual Target</label><input type="number" value={newRevLine.target_annual} onChange={e => setNewRevLine(f => ({...f, target_annual: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-rev-annual" /></div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Q1</label><input type="number" value={newRevLine.target_q1} onChange={e => setNewRevLine(f => ({...f, target_q1: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
                    <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Q2</label><input type="number" value={newRevLine.target_q2} onChange={e => setNewRevLine(f => ({...f, target_q2: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
                    <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Q3</label><input type="number" value={newRevLine.target_q3} onChange={e => setNewRevLine(f => ({...f, target_q3: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
                    <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Q4</label><input type="number" value={newRevLine.target_q4} onChange={e => setNewRevLine(f => ({...f, target_q4: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={addRevenueLine} className="px-4 py-2 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="save-new-rev-line">Add Line</button>
                    <button onClick={() => setNewRevLine(null)} className="px-4 py-2 rounded-md text-xs border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
                  </div>
                  <p className="text-[10px]" style={{ color: '#9CA3AF' }}>This adds a new revenue line to the division. The division target will recalculate automatically. Also add a matching outlet in the Outlets tab for the chip selector.</p>
                </div>
              )}

              <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
                <table className="w-full text-sm">
                  <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
                    <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Outlet / Line</th>
                    <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Owner</th>
                    <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Annual</th>
                    <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Q1</th>
                    <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Q2</th>
                    <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Q3</th>
                    <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Q4</th>
                    <th className="px-3 py-2.5 text-xs" style={{ color: '#6B7280' }}></th>
                  </tr></thead>
                  <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                    {revFields.map(f => (
                      <tr key={f.key}>
                        {editingField === f.key ? (
                          <>
                            <td className="px-3 py-2 text-xs font-medium" style={{ color: '#1B2A4A' }}>{f.label}</td>
                            <td className="px-3 py-1"><input value={editValue.owner} onChange={e => setEditValue(v => ({...v, owner: e.target.value}))} className="w-full px-2 py-1 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid={`edit-owner-${f.key}`} /></td>
                            <td className="px-3 py-1"><input type="number" value={editValue.target_annual} onChange={e => setEditValue(v => ({...v, target_annual: e.target.value}))} className="w-20 px-2 py-1 rounded border text-xs text-right" style={{ borderColor: '#E5E1DC' }} data-testid={`edit-annual-${f.key}`} /></td>
                            <td className="px-3 py-1"><input type="number" value={editValue.target_q1} onChange={e => setEditValue(v => ({...v, target_q1: e.target.value}))} className="w-16 px-2 py-1 rounded border text-xs text-right" style={{ borderColor: '#E5E1DC' }} /></td>
                            <td className="px-3 py-1"><input type="number" value={editValue.target_q2} onChange={e => setEditValue(v => ({...v, target_q2: e.target.value}))} className="w-16 px-2 py-1 rounded border text-xs text-right" style={{ borderColor: '#E5E1DC' }} /></td>
                            <td className="px-3 py-1"><input type="number" value={editValue.target_q3} onChange={e => setEditValue(v => ({...v, target_q3: e.target.value}))} className="w-16 px-2 py-1 rounded border text-xs text-right" style={{ borderColor: '#E5E1DC' }} /></td>
                            <td className="px-3 py-1"><input type="number" value={editValue.target_q4} onChange={e => setEditValue(v => ({...v, target_q4: e.target.value}))} className="w-16 px-2 py-1 rounded border text-xs text-right" style={{ borderColor: '#E5E1DC' }} /></td>
                            <td className="px-3 py-1 flex gap-1">
                              <button onClick={saveFieldTarget} className="p-1 rounded bg-green-50 hover:bg-green-100" data-testid={`save-target-${f.key}`}><Check size={12} style={{ color: '#2D8B5E' }} /></button>
                              <button onClick={() => setEditingField(null)} className="p-1 rounded bg-gray-50 hover:bg-gray-100"><X size={12} style={{ color: '#6B7280' }} /></button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-2 text-xs font-medium" style={{ color: '#1B2A4A' }}>{f.label}</td>
                            <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{f.owner || '-'}</td>
                            <td className="px-3 py-2 text-xs text-right font-medium" style={{ color: '#1B2A4A' }}>{fmt(f.target_annual)}</td>
                            <td className="px-3 py-2 text-xs text-right" style={{ color: '#6B7280' }}>{fmt(f.target_q1)}</td>
                            <td className="px-3 py-2 text-xs text-right" style={{ color: '#6B7280' }}>{fmt(f.target_q2)}</td>
                            <td className="px-3 py-2 text-xs text-right" style={{ color: '#6B7280' }}>{fmt(f.target_q3)}</td>
                            <td className="px-3 py-2 text-xs text-right" style={{ color: '#6B7280' }}>{fmt(f.target_q4)}</td>
                            <td className="px-3 py-2">
                              <button onClick={() => startEditField(f)} className="p-1 rounded hover:bg-gray-100" data-testid={`edit-target-${f.key}`}><Pencil size={12} style={{ color: '#2A7B88' }} /></button>
                              <button onClick={() => deleteRevenueLine(f.key, f.label)} className="p-1 rounded hover:bg-gray-100 ml-0.5" title="Remove line"><Trash2 size={12} style={{ color: '#D4726A' }} /></button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr style={{ backgroundColor: '#F7F5F2' }}>
                    <td className="px-3 py-2 text-xs font-bold" style={{ color: '#1B2A4A' }}>TOTAL</td>
                    <td></td>
                    <td className="px-3 py-2 text-xs text-right font-bold" style={{ color: '#1B2A4A' }}>{fmt(revFields.reduce((s, f) => s + (f.target_annual || 0), 0))}</td>
                    <td className="px-3 py-2 text-xs text-right font-bold" style={{ color: '#6B7280' }}>{fmt(revFields.reduce((s, f) => s + (f.target_q1 || 0), 0))}</td>
                    <td className="px-3 py-2 text-xs text-right font-bold" style={{ color: '#6B7280' }}>{fmt(revFields.reduce((s, f) => s + (f.target_q2 || 0), 0))}</td>
                    <td className="px-3 py-2 text-xs text-right font-bold" style={{ color: '#6B7280' }}>{fmt(revFields.reduce((s, f) => s + (f.target_q3 || 0), 0))}</td>
                    <td className="px-3 py-2 text-xs text-right font-bold" style={{ color: '#6B7280' }}>{fmt(revFields.reduce((s, f) => s + (f.target_q4 || 0), 0))}</td>
                    <td></td>
                  </tr></tfoot>
                </table>
              </div>
              <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Click the pencil icon to edit any target. Division total recalculates automatically.</p>
            </>
          )}
        </div>
      )}

      {/* === OUTLETS TAB === */}
      {activeTab === 'outlets' && (
        <div className="space-y-4" data-testid="outlets-tab">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Outlet Management</h2>
            <div className="flex items-center gap-2">
              <select value={selectedDiv} onChange={e => setSelectedDiv(e.target.value)}
                className="px-3 py-1.5 rounded-md border text-xs" style={{ borderColor: '#E5E1DC' }}>
                {divisions.filter(d => !d.hidden).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {selectedDiv === 'tdb' && (
                <button
                  onClick={async () => {
                    if (!window.confirm("Re-attribute today's Shopify orders using the current city→outlet map?\n\nSafe & idempotent — only touches the Online slice. Manual Zomato/Swiggy/Store entries are untouched.")) return;
                    try {
                      const r = await api.post('/shopify/rollup/rebucket', {});
                      const d = r.data || {};
                      const tot = Object.entries(d.by_outlet || {})
                        .map(([k, v]) => `${k}: ₹${Math.round(v).toLocaleString('en-IN')}`).join('\n');
                      alert(`✓ Rebucket complete\n\nDate: ${d.date}\nOrders: ${d.rebooked_orders}\n\n${tot}\n\nGrand total: ₹${Math.round(d.grand_total).toLocaleString('en-IN')}`);
                    } catch (e) {
                      alert('Rebucket failed: ' + (e?.response?.data?.detail || e.message));
                    }
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white"
                  style={{ backgroundColor: '#BA7517' }}
                  data-testid="rebucket-shopify-btn"
                  title="Force re-attribution of today's Shopify orders using the current city map"
                >
                  <RotateCw size={12} /> Rebucket Shopify (today)
                </button>
              )}
              <button onClick={() => setOutletForm({ name: '', location: '', owner: '', status: 'active', sort_order: divOutlets.length + 1, sub_categories: [], has_occupancy: false, room_config: {} })}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-outlet-btn">
                <Plus size={12} /> Add Outlet
              </button>
            </div>
          </div>

          {/* Outlet form (add/edit) */}
          {outletForm && (
            <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="outlet-form">
              <div className="flex items-center justify-between"><p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{outletForm.id ? 'Edit Outlet' : 'New Outlet'}</p><button onClick={() => setOutletForm(null)}><X size={14} style={{ color: '#6B7280' }} /></button></div>

              {/* Clone-from helper (new-outlet only — skip on edit) */}
              {!outletForm.id && (
                <div className="rounded-md p-3" style={{ backgroundColor: '#F7F5F2', border: '1px dashed #D1CFCA' }}>
                  <label className="text-[10px] font-semibold tracking-wider" style={{ color: '#6B7280' }}>CLONE FROM EXISTING OUTLET (OPTIONAL)</label>
                  <select
                    value={outletForm.__clone_source || ''}
                    onChange={e => {
                      const srcId = e.target.value;
                      if (!srcId) {
                        setOutletForm(f => ({ ...f, __clone_source: '' }));
                        return;
                      }
                      const src = allOutlets.find(o => o.id === srcId);
                      if (!src) return;
                      // Copy only the config fields. Identity fields stay blank
                      // so the user is forced to name the new outlet correctly.
                      setOutletForm(f => ({
                        ...f,
                        __clone_source: srcId,
                        status: src.status || 'coming_soon',
                        sub_categories: [...(src.sub_categories || [])],
                        has_occupancy: !!src.has_occupancy,
                        room_config: src.has_occupancy ? { ...(src.room_config || {}) } : {},
                        // identity fields intentionally NOT copied
                        name: f.name, owner: f.owner, location: f.location,
                      }));
                    }}
                    className="w-full mt-1 px-3 py-2 rounded-md border text-sm bg-white"
                    style={{ borderColor: '#E5E1DC' }}
                    data-testid="outlet-clone-source">
                    <option value="">Start blank (default)</option>
                    <optgroup label="────────────────────────">
                      {allOutlets.map(o => {
                        const div = divisions.find(d => d.id === o.division_id);
                        return (
                          <option key={o.id} value={o.id}>
                            {o.name}{div ? ` · ${div.name}` : ''}
                          </option>
                        );
                      })}
                    </optgroup>
                  </select>
                  {outletForm.__clone_source && (
                    <p className="text-[10px] mt-1.5" style={{ color: '#2A7B88' }}>
                      ✓ Config cloned. Change name, owner, location, city below to finalise.
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Name *</label><input value={outletForm.name} onChange={e => setOutletForm(f => ({...f, name: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="outlet-name" /></div>
                <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Owner</label><input value={outletForm.owner} onChange={e => setOutletForm(f => ({...f, owner: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="outlet-owner" /></div>
                <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Location</label><input value={outletForm.location} onChange={e => setOutletForm(f => ({...f, location: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
                <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Status</label><select value={outletForm.status} onChange={e => setOutletForm(f => ({...f, status: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="outlet-status"><option value="active">Active</option><option value="coming_soon">Coming Soon</option><option value="pre_scale">Pre-Scale</option></select></div>
              </div>
              {/* Sub-categories */}
              <div>
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Sub-Categories (comma separated)</label>
                <input value={(outletForm.sub_categories || []).join(', ')}
                  onChange={e => setOutletForm(f => ({...f, sub_categories: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                  placeholder="e.g. Suite, Deluxe, Add-ons"
                  className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="outlet-subcats" />
                <p className="text-[9px] mt-0.5" style={{ color: '#9CA3AF' }}>These appear as fields in Daily Sales entry. Leave empty for simple total-only entry.</p>
              </div>
              {/* Occupancy */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={outletForm.has_occupancy || false}
                    onChange={e => setOutletForm(f => ({...f, has_occupancy: e.target.checked, room_config: e.target.checked ? (f.room_config || {total_rooms: 10, suites: 7, deluxe: 3}) : {}}))} />
                  <span className="text-xs" style={{ color: '#1B2A4A' }}>Has room occupancy tracking</span>
                </label>
                {outletForm.has_occupancy && (
                  <div className="flex gap-2">
                    <div><label className="text-[9px]" style={{ color: '#6B7280' }}>Suites</label><input type="number" value={outletForm.room_config?.suites || ''} onChange={e => setOutletForm(f => ({...f, room_config: {...(f.room_config||{}), suites: parseInt(e.target.value)||0, total_rooms: (parseInt(e.target.value)||0) + (f.room_config?.deluxe||0)}}))} className="w-14 px-2 py-1 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} /></div>
                    <div><label className="text-[9px]" style={{ color: '#6B7280' }}>Deluxe</label><input type="number" value={outletForm.room_config?.deluxe || ''} onChange={e => setOutletForm(f => ({...f, room_config: {...(f.room_config||{}), deluxe: parseInt(e.target.value)||0, total_rooms: (f.room_config?.suites||0) + (parseInt(e.target.value)||0)}}))} className="w-14 px-2 py-1 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} /></div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={saveOutlet} className="px-4 py-2 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="outlet-save">Save Outlet</button>
                <button onClick={() => setOutletForm(null)} className="px-4 py-2 rounded-md text-xs border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
            <table className="w-full text-sm" data-testid="outlets-table">
              <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Name</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Owner</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Sub-Categories</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
                <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Actions</th>
              </tr></thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {divOutlets.map(o => (
                  <tr key={o.id}>
                    <td className="px-3 py-2 text-xs font-medium" style={{ color: '#1B2A4A' }}>{o.name}</td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{o.owner || '-'}</td>
                    <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(o.sub_categories || []).map(s => <span key={s} className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>{s}</span>)}{(o.sub_categories || []).length === 0 && <span className="text-[9px]" style={{ color: '#9CA3AF' }}>—</span>}{o.has_occupancy && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#E6F1FB', color: '#185FA5' }}>Rooms</span>}</div></td>
                    <td className="px-3 py-2"><span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{
                      backgroundColor: o.status === 'active' ? '#ECFDF5' : o.status === 'coming_soon' ? '#FEF3C7' : '#F3F4F6',
                      color: o.status === 'active' ? '#2D8B5E' : o.status === 'coming_soon' ? '#92400E' : '#6B7280',
                    }}>{o.status || 'active'}</span></td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setOutletForm({ id: o.id, name: o.name, location: o.location || '', owner: o.owner || '', status: o.status || 'active', sort_order: o.sort_order || 0, sub_categories: o.sub_categories || [], has_occupancy: o.has_occupancy || false, room_config: o.room_config || {} })} className="p-1 rounded hover:bg-gray-100"><Pencil size={12} style={{ color: '#2A7B88' }} /></button>
                      <button onClick={() => deleteOutlet(o.id, o.name)} className="p-1 rounded hover:bg-gray-100 ml-1"><Trash2 size={12} style={{ color: '#D4726A' }} /></button>
                    </td>
                  </tr>
                ))}
                {divOutlets.length === 0 && <tr><td colSpan="5" className="px-3 py-6 text-center text-xs" style={{ color: '#9CA3AF' }}>No outlets for this division. Click "Add Outlet" to create one.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === DIVISIONS TAB === */}
      {activeTab === 'divisions' && (
        <div className="space-y-4" data-testid="divisions-tab">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Division Settings</h2>
            <button
              onClick={() => setDivEdit({ isNew: true, name: '', leader_name: '', entity_name: '', color: '#2A7B88', parent_division_id: '' })}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white"
              style={{ backgroundColor: '#2A7B88' }}
              data-testid="add-division-btn">
              <Plus size={12} /> Add Division
            </button>
          </div>
          {divEdit && (
            <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="division-edit-form">
              <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{divEdit.isNew ? 'New Division / Department' : `Edit ${divEdit.name}`}</p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Name *</label><input value={divEdit.name} onChange={e => setDivEdit(d => ({...d, name: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="div-edit-name" /></div>
                <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Leader</label><input value={divEdit.leader_name} onChange={e => setDivEdit(d => ({...d, leader_name: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="div-edit-leader" /></div>
                <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Entity</label><input value={divEdit.entity_name} onChange={e => setDivEdit(d => ({...d, entity_name: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} /></div>
                <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Color</label><input type="color" value={divEdit.color} onChange={e => setDivEdit(d => ({...d, color: e.target.value}))} className="w-full h-10 rounded-md border" style={{ borderColor: '#E5E1DC' }} /></div>
              </div>
              <div>
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Parent division (optional — for nested departments)</label>
                <select value={divEdit.parent_division_id || ''} onChange={e => setDivEdit(d => ({...d, parent_division_id: e.target.value}))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="div-edit-parent">
                  <option value="">— None (top-level) —</option>
                  {divisions.filter(d => !d.hidden && d.id !== divEdit.id).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <p className="text-[9px] mt-0.5" style={{ color: '#9CA3AF' }}>e.g. create "Technology" as a child of "Group" to make it a sub-department.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={saveDivision} className="px-4 py-2 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="div-save">{divEdit.isNew ? 'Create Division' : 'Save'}</button>
                <button onClick={() => setDivEdit(null)} className="px-4 py-2 rounded-md text-xs border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Division</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Parent</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Leader</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Entity</th>
                <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Target (auto)</th>
                <th className="px-3 py-2.5"></th>
              </tr></thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {divisions.filter(d => !d.hidden).map(d => {
                  const parent = divisions.find(x => x.id === d.parent_division_id);
                  return (
                  <tr key={d.id}>
                    <td className="px-3 py-2"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded" style={{ backgroundColor: d.color || '#6B7280' }} /><span className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{d.name}</span></div></td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{parent?.name || '—'}</td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{d.leader_name || '-'}</td>
                    <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>{d.entity_name || '-'}</td>
                    <td className="px-3 py-2 text-xs text-right font-medium" style={{ color: '#1B2A4A' }}>{fmt(d.annual_target)}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setDivEdit({ id: d.id, name: d.name, leader_name: d.leader_name || '', entity_name: d.entity_name || '', color: d.color || '#6B7280', parent_division_id: d.parent_division_id || '' })} className="p-1 rounded hover:bg-gray-100" data-testid={`edit-div-${d.id}`} title="Edit"><Pencil size={12} style={{ color: '#2A7B88' }} /></button>
                      <button onClick={() => hideDivision(d.id, d.name)} className="p-1 rounded hover:bg-gray-100 ml-1" title="Hide (soft-delete)"><Trash2 size={12} style={{ color: '#D4726A' }} /></button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === EXPENSES TAB === */}
      {activeTab === 'expenses' && <ExpensesAdminTab />}
      {activeTab === 'channels' && <RevenueChannelsTab />}

      {/* === THRESHOLDS TAB === */}
      {activeTab === 'thresholds' && (
        <div className="space-y-4" data-testid="thresholds-tab">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Alert Thresholds</h2>
            <button onClick={saveThresholds} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="save-thresholds-btn"><Save size={13} /> Save</button>
          </div>

          {/* Performance Score Red Alert Runner */}
          <ScoreAlertRunner />

          <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }}>
            {Object.entries(THRESHOLD_LABELS).map(([key, config]) => (
              <div key={key} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center py-2 border-b" style={{ borderColor: '#F3F4F6' }}>
                <div className="sm:col-span-2">
                  <p className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{config.label}</p>
                  <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{config.desc}</p>
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" value={thresholds[key] ?? ''} onChange={e => setThresholds(t => ({ ...t, [key]: parseFloat(e.target.value) || 0 }))}
                    className="w-20 px-2 py-1.5 rounded-md border text-xs text-right font-medium" style={{ borderColor: '#E5E1DC' }} data-testid={`threshold-${key}`} />
                  <span className="text-[10px]" style={{ color: '#6B7280' }}>{config.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// EXPENSES ADMIN (aggregate view)
// ============================================================
function ExpensesAdminTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/people-kit/admin/overview').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const fC = (v) => {
    if (!v || v === 0) return '₹0';
    const n = Number(v);
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
    return `₹${n.toLocaleString('en-IN')}`;
  };

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p>;
  if (!data) return <p className="py-8 text-center text-sm" style={{ color: '#D4726A' }}>Unable to load.</p>;

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-4" data-testid="admin-expenses-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Group-wide Kit & Expenses</h2>
        <a href={`${API_URL}/api/people-kit/admin/export-expenses-csv?token=${localStorage.getItem('access_token')}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
          data-testid="export-expenses-btn"><Download size={12} /> Export CSV</a>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiMini label="Outstanding Petty Cash" value={fC(data.outstanding_petty_cash)} color={data.outstanding_petty_cash > 0 ? '#D97706' : '#2D8B5E'} />
        <KpiMini label="Overdue Settlements" value={data.overdue_settlements} color={data.overdue_settlements > 0 ? '#DC2626' : '#2D8B5E'} />
        <KpiMini label="Pending Claims" value={`${data.pending_claims_count} · ${fC(data.pending_claims_amount)}`} color="#D97706" />
        <KpiMini label="Paid YTD" value={fC(data.paid_claims_ytd)} color="#2D8B5E" />
        <KpiMini label="Uniforms Due" value={data.uniforms_due_replacement} color={data.uniforms_due_replacement > 0 ? '#D97706' : '#2D8B5E'} />
        <KpiMini label="Pending Returns" value={data.pending_asset_returns} color={data.pending_asset_returns > 0 ? '#D97706' : '#2D8B5E'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Paid Claims by Category (YTD)</p>
          {data.by_category.length === 0 ? (
            <p className="text-xs py-4" style={{ color: '#9CA3AF' }}>No paid claims yet.</p>
          ) : (
            <div className="space-y-1">
              {data.by_category.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1 border-b" style={{ borderColor: '#F3F4F6' }}>
                  <span style={{ color: '#1B2A4A' }}>{c.category}</span>
                  <span className="font-medium" style={{ color: '#1B2A4A' }}>{fC(c.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Paid Claims by Outlet (YTD)</p>
          {data.by_outlet.length === 0 ? (
            <p className="text-xs py-4" style={{ color: '#9CA3AF' }}>No paid claims yet.</p>
          ) : (
            <div className="space-y-1">
              {data.by_outlet.map((o, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1 border-b" style={{ borderColor: '#F3F4F6' }}>
                  <span style={{ color: '#1B2A4A' }}>{o.outlet}</span>
                  <span className="font-medium" style={{ color: '#1B2A4A' }}>{fC(o.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
        Tip: Paid claims are automatically tagged to outlets for P&L — they flow into "Other Direct Costs" in Division P&L view.
      </p>
    </div>
  );
}

function KpiMini({ label, value, color }) {
  return (
    <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
      <p className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className="text-lg font-bold" style={{ color }}>{value}</p>
    </div>
  );
}


// ============================================================
// PERFORMANCE SCORE RED ALERT RUNNER
// ============================================================
function ScoreAlertRunner() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const fetchHistory = () => api.get('/outlet-intel/score-alerts-history?limit=10').then(r => setHistory(r.data.alerts || [])).catch(() => {});
  useEffect(() => { fetchHistory(); }, []);

  const run = async (force = false) => {
    setLoading(true);
    try {
      const r = await api.post(`/outlet-intel/run-score-alerts?force=${force}`);
      setLastRun(r.data);
      fetchHistory();
    } catch (e) {
      alert('Failed: ' + (e?.response?.data?.detail || 'Unknown'));
    }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="score-alert-runner">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Performance Score Red Alert</p>
          <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
            Scans all outlets. When an outlet newly drops into Red, fires WhatsApp + email to outlet owner + division leader.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => run(false)} disabled={loading} className="px-3 py-1.5 rounded-md text-xs font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#2A7B88' }} data-testid="run-alerts-btn">
            {loading ? 'Running...' : 'Run Check'}
          </button>
          <button onClick={() => run(true)} disabled={loading} className="px-3 py-1.5 rounded-md text-xs border disabled:opacity-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} title="Force alert even if previously Red" data-testid="force-alerts-btn">
            Force Re-send
          </button>
        </div>
      </div>

      {lastRun && (
        <div className="rounded-md p-2 mb-3 text-[11px]" style={{ backgroundColor: lastRun.alerts_fired > 0 ? '#FEF2F2' : '#ECFDF5', color: lastRun.alerts_fired > 0 ? '#DC2626' : '#2D8B5E' }}>
          Scan complete: {lastRun.snapshots_updated} outlets scored · <strong>{lastRun.alerts_fired}</strong> alert{lastRun.alerts_fired !== 1 ? 's' : ''} fired
        </div>
      )}

      {history.length === 0 ? (
        <p className="text-[11px] py-2" style={{ color: '#9CA3AF' }}>No alerts fired yet. Alerts fire only when an outlet newly transitions to Red.</p>
      ) : (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase font-medium" style={{ color: '#9CA3AF' }}>Recent Alerts ({history.length})</p>
          {history.map((a, i) => (
            <div key={i} className="border-l-2 pl-2 py-1" style={{ borderColor: '#DC2626' }}>
              <p className="text-[11px] font-medium" style={{ color: '#1B2A4A' }}>{a.outlet} → <span style={{ color: '#DC2626' }}>{a.score}/10</span></p>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>
                Weakest: {a.weakest_dimension} ({a.weakest_value}) · {a.recipients?.length || 0} recipient(s) · {new Date(a.fired_at).toLocaleString()}
              </p>
              {a.send_results && a.send_results.length > 0 && (
                <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                  {a.send_results.map((s, j) => <span key={j}>{s.channel}:{s.result}{j < a.send_results.length - 1 ? ' · ' : ''}</span>)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] mt-3" style={{ color: '#9CA3AF' }}>
        Tip: Schedule <code style={{ backgroundColor: '#F7F5F2', padding: '1px 4px', borderRadius: '3px' }}>curl -X POST /api/outlet-intel/run-score-alerts</code> as a daily cron for auto-fire.
      </p>
    </div>
  );
}
